import React, { type PropsWithChildren } from 'react';
import { Platform, StyleSheet, useWindowDimensions } from 'react-native';

import { UL } from '@expo/html-elements';
import { theme } from '@garnish/constants';

import { useNutritionCalculatorSmallScreen } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const List = (props: Props) => {
  const { children, withoutSpacing, testID } = props;

  const { fontScale, width } = useWindowDimensions();
  const isSmallScreen = useNutritionCalculatorSmallScreen();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isExtraSmallScreen = width < 375;
  const isScaledFontSize = fontScale >= 1.3;

  const shouldRenderNarrowVariant = isExtraSmallScreen || isScaledFontSize;

  // NOTE: On native, stretching outside the small screen layout feeds the
  //       floating dialog's measured width back into itself, so it outgrows
  //       its container and spreads the items apart.
  const shouldStretch = Platform.OS === 'web' || isSmallScreen;

  // ─── Styles ──────────────────────────────────────────────────────────

  const listStyles = [
    styles.list,
    shouldStretch ? styles.listStretched : undefined,
    shouldRenderNarrowVariant ? styles.listNarrow : styles.listWide,
    withoutSpacing ? styles.listWithoutSpacing : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <UL style={listStyles} testID={testID}>
      {children}
    </UL>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  list: {
    display: 'flex', // override default style
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: theme.spacing['2'],
    paddingRight: Platform.select({
      web: undefined,
      default: 84, // we use extra offset, since `minWidth: 100%` is not working as expected on native.
    }),
  },
  listStretched: {
    minWidth: '100%',
  },
  listNarrow: {
    gap: theme.spacing['3'],
    paddingHorizontal: theme.spacing['4'],
  },
  listWide: {
    gap: theme.spacing['5'],
    paddingRight: theme.spacing['2'],
    paddingLeft: theme.spacing['6'],
  },
  listWithoutSpacing: {
    paddingHorizontal: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  testID?: string;
  withoutSpacing?: boolean;
}>;
